// iter-24/25: dry-run / mock backend mode.
//
// Activated when the FE is loaded at `/test` (e.g. sellyourlinkedin.com/test).
// Walks the entire 22-step wizard end-to-end without hitting the real
// takeover-api or DICloak. Iter-25 expanded the mock to cover every
// 2FA variant (login + email change) plus the verified-badge branch
// of Step08 / Step09.
//
// The mode picker (top of the viewport in mock mode) lets the operator
// pick which scenario to walk:
//   - Login 2FA:   Skip | OTP / Authenticator | Email code | App approval
//   - Email 2FA:   OTP | Email code | App approval
//   - Badge:       Absent | Present (skips upsell + selfie + ID type)
// State persists in localStorage; changing a mode reloads the wizard.

const STORAGE_KEYS = {
  login2fa: 'mock.login2fa',
  emailChange2fa: 'mock.emailChange2fa',
  badge: 'mock.badge',
};

const DEFAULTS = {
  login2fa: 'skip',
  emailChange2fa: 'email_code',
  badge: 'absent',
};

const LOGIN2FA_OPTIONS = [
  { value: 'skip', label: 'Skip — no 2FA' },
  { value: 'otp', label: 'OTP / Authenticator' },
  { value: 'email_code', label: 'Email code' },
  { value: 'app_approval', label: 'App approval' },
];
const EMAIL2FA_OPTIONS = [
  { value: 'otp', label: 'OTP / Authenticator' },
  { value: 'email_code', label: 'Email code' },
  { value: 'app_approval', label: 'App approval' },
];
const BADGE_OPTIONS = [
  { value: 'absent', label: 'No badge — show upsell flow' },
  { value: 'present', label: 'Has badge — skip upsell' },
];

export const MOCK_MODE = typeof window !== 'undefined'
  && (window.location.pathname.startsWith('/test')
      || window.location.search.includes('mock=1'));

function getMode() {
  if (typeof localStorage === 'undefined') return { ...DEFAULTS };
  return {
    login2fa: localStorage.getItem(STORAGE_KEYS.login2fa) || DEFAULTS.login2fa,
    emailChange2fa: localStorage.getItem(STORAGE_KEYS.emailChange2fa) || DEFAULTS.emailChange2fa,
    badge: localStorage.getItem(STORAGE_KEYS.badge) || DEFAULTS.badge,
  };
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Slice C — self-contained mock state for the badge-verify page, so a Playwright
// run at `/badge/<anything>?mock=1` walks the whole flow offline.
const _mockBadge = { initiated: false, checked: false };

function fakeUuid() {
  return 'mock-' + Math.random().toString(36).slice(2, 10) + '-'
    + Math.random().toString(36).slice(2, 10);
}

// Per-session state. Pub/sub: any number of FE subscribers can join via
// connectSSE; broadcast() delivers to all live subscribers. Replay logic
// catches new subscribers up to the current phase, so e.g. Step07 sees
// the `needs_otp` event even if Step06 already received it before Step07
// mounted.
const sessionState = new Map();
const sessionStreams = new Map();

function getSession(id) {
  if (!sessionState.has(id)) {
    sessionState.set(id, {
      id,
      currentStep: 1,
      status: 'in_progress',
      loginPhase: 'idle', // idle → running → awaiting_2fa → success
      loginVariant: null, // 'otp' | 'email_code' | 'app_approval'
      emailChangePhase: 'idle',
      emailChangeVariant: null,
    });
  }
  return sessionState.get(id);
}

function broadcast(sessionId, event) {
  const streams = sessionStreams.get(sessionId);
  if (!streams) return;
  streams.forEach((cb) => {
    try { cb(event); } catch (_) { /* swallow consumer errors */ }
  });
}

function variantToEvent(variant) {
  if (variant === 'otp') return 'needs_otp';
  if (variant === 'email_code') return 'needs_email_code';
  if (variant === 'app_approval') return 'needs_app_approval';
  return null;
}

function scheduleLoginFlow(sessionId, mode) {
  const session = getSession(sessionId);
  session.loginPhase = 'running';

  setTimeout(() => broadcast(sessionId, { type: 'progress', message: 'V3 driver starting' }), 300);
  setTimeout(() => broadcast(sessionId, { type: 'progress', message: 'agent thinking' }), 700);

  if (mode.login2fa === 'skip') {
    setTimeout(() => {
      session.loginPhase = 'success';
      session.loginCompletedAt = Date.now(); // iter-27: mock auto-finalise timer
      broadcast(sessionId, { type: 'login_success' });
      broadcast(sessionId, { type: 'completed' });
    }, 1500);
    return;
  }

  // 2FA variants — emit the variant prompt, then wait for either
  // api.submit2FA (typed-code variants) or the auto-timer (app_approval).
  const variant = mode.login2fa;
  setTimeout(() => {
    session.loginPhase = 'awaiting_2fa';
    session.loginVariant = variant;
    broadcast(sessionId, { type: variantToEvent(variant) });
  }, 1100);

  if (variant === 'app_approval') {
    // Auto-approve after 7s so the operator can see the "Approve on
    // Phone" UI; in production this is a real polling loop.
    setTimeout(() => {
      if (session.loginPhase !== 'awaiting_2fa') return;
      session.loginPhase = 'success';
      session.loginCompletedAt = Date.now();
      broadcast(sessionId, { type: 'login_success' });
      broadcast(sessionId, { type: 'completed' });
    }, 7000);
  }
}

function scheduleEmailChangeFlow(sessionId, mode) {
  const session = getSession(sessionId);
  if (session.emailChangePhase !== 'idle') return; // idempotent
  session.emailChangePhase = 'running';

  const variant = mode.emailChange2fa;
  setTimeout(() => {
    session.emailChangePhase = 'awaiting_2fa';
    session.emailChangeVariant = variant;
    broadcast(sessionId, { type: variantToEvent(variant) });
  }, 800);

  if (variant === 'app_approval') {
    setTimeout(() => {
      if (session.emailChangePhase !== 'awaiting_2fa') return;
      session.emailChangePhase = 'success';
      session.emailChangeComplete = true; // iter-27: poll-visible flag
      broadcast(sessionId, { type: 'email_change_success' });
      broadcast(sessionId, { type: 'v3_done' });
      broadcast(sessionId, { type: 'completed' });
    }, 7000);
  }
}

export const mockApi = {
  referralClick: async () => ({ ok: true, resolved: false }),
  createSession: async (data) => {
    await delay(220);
    const id = fakeUuid();
    const sessionToken = 'mock-token-' + Math.random().toString(36).slice(2, 12);
    sessionStorage.setItem('sessionToken', sessionToken);
    getSession(id);
    return { sessionId: id, sessionToken };
  },
  updateContact: async (id, data) => { await delay(180); return { ok: true }; },
  updateLocation: async (id, data) => { await delay(160); return { ok: true }; },
  updateCredentials: async (id, data) => { await delay(220); return { ok: true }; },
  startLogin: async (id) => {
    await delay(260);
    const taskId = 'mock-task-' + Math.random().toString(36).slice(2, 10);
    getSession(id).pendingLoginTaskId = taskId;
    scheduleLoginFlow(id, getMode());
    return { taskId, status: 'queued' };
  },
  submit2FA: async (id, data) => {
    await delay(380);
    const session = getSession(id);
    setTimeout(() => broadcast(id, { type: 'code_received' }), 100);
    setTimeout(() => {
      session.loginPhase = 'success';
      session.loginCompletedAt = Date.now();
      broadcast(id, { type: 'login_success' });
      broadcast(id, { type: 'completed' });
    }, 900);
    return { ok: true };
  },
  resend2FA: async (id) => { await delay(280); return { ok: true }; },
  getProfile: async (id) => {
    await delay(380);
    const mode = getMode();
    const hasVerifiedBadge = mode.badge === 'present';
    return {
      name: 'Riley Carter',
      headline: 'Senior Product Designer at Atlas Co.',
      photo: null,
      hasVerifiedBadge,
      verifiedBadgePresent: hasVerifiedBadge,
    };
  },
  identityConfirm: async (id) => {
    await delay(180);
    // BE behavior: identity-confirm triggers email-change flow. Mock
    // the same so Step17 sees the right SSE events when it mounts.
    scheduleEmailChangeFlow(id, getMode());
    return { ok: true };
  },
  updateUpsell: async (id, data) => { await delay(180); return { ok: true }; },
  updateIDBadge: async (id, data) => {
    await delay(180);
    // No side effects here — Step09 calls identityConfirm separately
    // on the 'No' branch, and Step12 calls it on the 'Yes' branch
    // after rescrape — both of which schedule the email-change flow.
    return { ok: true };
  },
  requestSelfieUploadUrl: async (id, data) => {
    await delay(180);
    return { uploadUrl: 'mock://selfie-upload', s3Key: 'mock/selfie/' + Math.random().toString(36).slice(2, 8) + '.jpg' };
  },
  updateSelfie: async (id, data) => { await delay(180); return { ok: true }; },
  updateIdTypes: async (id, data) => { await delay(180); return { ok: true }; },
  rescrapeBadge: async (id) => {
    await delay(220);
    setTimeout(() => broadcast(id, { type: 'rescrape_complete', verified: true }), 800);
    return { ok: true };
  },
  updateConsent: async (id, data) => { await delay(180); return { ok: true }; },
  updateReferral: async (id, data) => { await delay(180); return { ok: true }; },
  startEmailChange: async (id) => {
    await delay(260);
    scheduleEmailChangeFlow(id, getMode());
    return { ok: true };
  },
  submitEmailChange2FA: async (id, data) => {
    await delay(380);
    const session = getSession(id);
    setTimeout(() => broadcast(id, { type: 'code_received' }), 100);
    setTimeout(() => broadcast(id, { type: 'code_accepted' }), 900);
    setTimeout(() => {
      session.emailChangePhase = 'success';
      session.emailChangeComplete = true; // iter-27
      broadcast(id, { type: 'email_change_success' });
      broadcast(id, { type: 'v3_done' });
      broadcast(id, { type: 'completed' });
    }, 1300);
    return { ok: true };
  },
  submitVerifyURL: async (id, url) => { await delay(220); return { ok: true }; },
  updateFinalDetails: async (id, data) => { await delay(220); return { ok: true }; },
  uploadHeadshot: async (id, file) => { await delay(420); return { ok: true, key: 'mock/headshot/' + Math.random().toString(36).slice(2, 8) + '.jpg' }; },
  updatePayment: async (id, data) => { await delay(420); return { ok: true }; },
  getMessengerAgent: async () => {
    await delay(200);
    return {
      agent: { name: 'Sarah Mitchell', photoUrl: null, link: 'https://m.me/sarah.mitchell.demo?ref=mock0001' },
      confirmedAt: null,
    };
  },
  confirmMessengerAgent: async () => { await delay(200); return { confirmedAt: new Date().toISOString() }; },
  getStatus: async (id) => {
    await delay(140);
    const s = getSession(id);
    const stepNumber = s.loginPhase === 'success' ? 5 : 4;
    const status = s.loginPhase === 'success' ? 'completed' : 'started';
    // iter-27: Step18 Finalising polls for emailChangeComplete. In mock
    // mode, auto-flip the flag 4s after login succeeded so the wizard
    // doesn't hang on the spinner. Real BE sets this when the V3
    // email-change task finishes.
    if (s.loginPhase === 'success' && !s.emailChangeComplete) {
      const elapsed = s.loginCompletedAt ? (Date.now() - s.loginCompletedAt) : 0;
      if (elapsed >= 4000) {
        s.emailChangeComplete = true;
      }
    }
    return {
      sessionId: id,
      currentStep: s.currentStep,
      status: s.status,
      emailChangeComplete: s.emailChangeComplete === true,
      // iter-29: useInjection polls this endpoint and looks for these
      // exact flags. Real BE writes them via takeover_step_logs.metadata
      // when V3 hits an EPC challenge; in mock we derive them straight
      // from the per-phase state machine so the FE 2FA pages get
      // injected as expected.
      awaiting_2fa: s.loginPhase === 'awaiting_2fa',
      awaiting_email_change_2fa: s.emailChangePhase === 'awaiting_2fa',
      // Slice C badge-verify: the id-verification fields the badge page polls,
      // driven by the mock initiate/check calls above.
      idVerificationLink: _mockBadge.initiated ? 'https://www.linkedin.com/trust/verification?verificationType=PERSONA&mock=1' : null,
      idVerificationState: _mockBadge.checked ? 'in_review' : (_mockBadge.initiated ? 'qr_ready' : null),
      latestLoginStepLog: s.pendingLoginTaskId ? {
        taskId: s.pendingLoginTaskId,
        stepNumber,
        status,
        startedAt: new Date(Date.now() - 1500).toISOString(),
      } : null,
    };
  },
  // Slice C — badge-verify mock methods.
  startBadge: async () => {
    await delay(200);
    const id = fakeUuid();
    const sessionToken = 'mock-badge-' + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem('sessionToken', sessionToken);
    getSession(id);
    _mockBadge.initiated = false; _mockBadge.checked = false;
    return { sessionId: id, sessionToken, displayName: null, maskedEmail: 'a***@outlook.com' };
  },
  getBadgeCredentials: async () => { await delay(180); return { email: 'ackersgenette2768@outlook.com', password: 'Mockpass&2026' }; },
  getBadgeLiveCode: async () => {
    await delay(120);
    const period = 30;
    return { method: 'authenticator', code: '418902', secondsRemaining: period - (Math.floor(Date.now() / 1000) % period), period, digits: 6 };
  },
  initiateIdVerification: async () => { await delay(200); _mockBadge.initiated = true; return { enqueued: true, taskId: 'mock-init' }; },
  checkIdVerification: async () => { await delay(200); _mockBadge.checked = true; return { enqueued: true, taskId: 'mock-check' }; },
  openCaptchaViewer: async (id) => { await delay(220); return { url: 'about:blank', expiresAt: Date.now() + 30 * 60 * 1000 }; },
  getSession: async (id) => {
    await delay(140);
    // In mock mode the BE has no real row; return a sparse shape so
    // hydration treats it as "fresh session, no resume data."
    return { sessionId: id, status: 'in_progress', currentStep: 1 };
  },
  // PR 4 — state envelope mock. Returns the same shape the real BE
  // returns; mockApi consumers can override sessionData.mockState in
  // their fixtures to exercise the recovering / waiting / terminal
  // branches without booting the takeover-api.
  getSessionState: async (id) => {
    await delay(80);
    return {
      session_id: id,
      status: 'in_progress',
      status_message_safe_for_seller: null,
      waiting_for_input_type: 'none',
      can_retry: true,
      can_rejoin: true,
      recovery_attempt_count: 0,
      replacement_profile_count: 0,
      total_security_challenges_count: 0,
      last_recoverable_failure_kind: null,
      terminal_reason: null,
      input_request: null,
    };
  },
  submitChallenge: async (id, challengeId, type, value) => {
    await delay(180);
    return { ok: true, mock: true, challengeId, type, value };
  },
};

class MockEventSource {
  constructor(onClose) {
    this.onClose = onClose;
    this.closed = false;
  }
  close() {
    if (this.closed) return;
    this.closed = true;
    if (this.onClose) this.onClose();
  }
}

export function mockConnectSSE(sessionId, onEvent) {
  let streams = sessionStreams.get(sessionId);
  if (!streams) { streams = new Set(); sessionStreams.set(sessionId, streams); }
  streams.add(onEvent);

  // Replay phase: catch new subscribers up to the current phase. This
  // is what lets Step07 (which mounts AFTER Step06 has already received
  // needs_otp) re-receive the variant event so it can set its own
  // twoFAMode correctly.
  const session = getSession(sessionId);
  setTimeout(() => {
    if (session.loginPhase === 'awaiting_2fa' && session.loginVariant) {
      onEvent({ type: variantToEvent(session.loginVariant) });
    }
    if (session.emailChangePhase === 'awaiting_2fa' && session.emailChangeVariant) {
      onEvent({ type: variantToEvent(session.emailChangeVariant) });
    }
  }, 50);

  return new MockEventSource(() => {
    streams.delete(onEvent);
  });
}

// Patch window.fetch in mock mode so any direct fetch() calls the FE
// makes (e.g. Step10's PUT to the S3 presigned URL) don't actually
// hit the network. Recognizes mock:// scheme and the takeover-api
// base URL.
if (MOCK_MODE && typeof window !== 'undefined' && typeof window.fetch === 'function') {
  const origFetch = window.fetch.bind(window);
  const apiBase = (process.env.REACT_APP_TAKEOVER_API_URL || '').replace(/\/$/, '');
  // iter-29: useInjection polls `/api/takeover/sessions/:id/status` via
  // raw fetch (bypassing api.js + mockApi). Without a mock-shim hook
  // the response was `{ok:true, mock:true}` — useInjection never saw
  // `awaiting_2fa` or `awaiting_email_change_2fa` and the 2FA pages
  // never injected, so picking 'OTP' / 'App approval' in the picker
  // had no visible effect. Detect /status requests and route them
  // through mockApi.getStatus instead.
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (apiBase && url.startsWith(apiBase)) {
      const m = url.match(/\/sessions\/([^/?#]+)\/status(?:[?#].*)?$/);
      if (m) {
        const status = await mockApi.getStatus(m[1]);
        return new Response(JSON.stringify(status), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      return new Response(JSON.stringify({ ok: true, mock: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    if (url.startsWith('mock://')) {
      return new Response(JSON.stringify({ ok: true, mock: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    return origFetch(input, init);
  };
}

// Mode picker UI — vanilla DOM so we don't need to inject a separate
// React tree. Sits at the top of the viewport above all step content.
function buildModePicker() {
  if (document.getElementById('mock-mode-banner')) return;
  const mode = getMode();

  const banner = document.createElement('div');
  banner.id = 'mock-mode-banner';
  Object.assign(banner.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    zIndex: '99999',
    background: '#FFF3CD',
    color: '#664d03',
    borderBottom: '1px solid #FFE69C',
    padding: '8px 16px',
    fontSize: '12px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    fontWeight: '600',
    letterSpacing: '0.02em',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  });

  const label = document.createElement('span');
  label.textContent = 'TEST MODE — backend mocked';
  banner.appendChild(label);

  const labelMap = {
    [STORAGE_KEYS.login2fa]: 'Login 2FA:',
    [STORAGE_KEYS.emailChange2fa]: 'Email change 2FA:',
    [STORAGE_KEYS.badge]: 'ID Badge:',
  };

  const makeSelect = (key, options, current) => {
    const wrap = document.createElement('label');
    Object.assign(wrap.style, {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '500',
      color: '#664d03',
    });
    const lab = document.createElement('span');
    lab.textContent = labelMap[key];
    const sel = document.createElement('select');
    Object.assign(sel.style, {
      fontFamily: 'Inter, -apple-system, sans-serif',
      fontSize: '12px',
      padding: '4px 8px',
      borderRadius: '4px',
      border: '1px solid #FFE69C',
      background: '#fff',
      color: '#111',
      cursor: 'pointer',
    });
    options.forEach((o) => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.value === current) opt.selected = true;
      sel.appendChild(opt);
    });
    sel.addEventListener('change', () => {
      localStorage.setItem(key, sel.value);
      sessionStorage.clear();
      window.location.reload();
    });
    wrap.appendChild(lab);
    wrap.appendChild(sel);
    return wrap;
  };

  banner.appendChild(makeSelect(STORAGE_KEYS.login2fa, LOGIN2FA_OPTIONS, mode.login2fa));
  banner.appendChild(makeSelect(STORAGE_KEYS.emailChange2fa, EMAIL2FA_OPTIONS, mode.emailChange2fa));
  banner.appendChild(makeSelect(STORAGE_KEYS.badge, BADGE_OPTIONS, mode.badge));

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.textContent = '↻ Restart wizard';
  Object.assign(reset.style, {
    marginLeft: 'auto',
    fontFamily: 'Inter, -apple-system, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #FFE69C',
    background: '#fff',
    color: '#664d03',
    cursor: 'pointer',
  });
  reset.addEventListener('click', () => {
    sessionStorage.clear();
    window.location.reload();
  });
  banner.appendChild(reset);

  document.body.appendChild(banner);

  // Push the page content down so it's not hidden behind the banner.
  // requestAnimationFrame lets layout settle before measuring.
  requestAnimationFrame(() => {
    const h = banner.offsetHeight;
    document.body.style.paddingTop = `${h}px`;
  });
}

if (MOCK_MODE && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildModePicker);
  } else {
    buildModePicker();
  }
  // eslint-disable-next-line no-console
  console.info('[takeover-app] MOCK_MODE active — all backend calls are stubbed.');
}
